import { SiteGroup } from "@models/site-group";
import { useLocation } from "react-router";
import { useGetEquipmentHierarchy, useGetSiteApi } from "../api";
import { ProductionArea } from "../enums";
import { useSelectedSites } from "./useSelectedSites";

export type UseCurrentProductionAreaResult = {
  productionArea: ProductionArea | undefined;
  isLoading: boolean;
};

export const useCurrentProductionArea = (
  siteOverride?: string,
  requestedProductionArea?: string
): UseCurrentProductionAreaResult => {
  const { selectedSite, selectedSites } = useSelectedSites();
  const { search } = useLocation();
  const site = siteOverride ?? selectedSite;
  const isLandingPage = import.meta.env.VITE_MODULE_NAME === "Connect";
  const urlProductionArea =
    isLandingPage && selectedSites.length === 1 && site === selectedSite
      ? (new URLSearchParams(search).get("productionArea") ?? undefined)
      : undefined;
  const productionArea = requestedProductionArea ?? urlProductionArea;

  const isHomepage = isLandingPage && !site;

  const { data, isLoading } = useGetSiteApi(site, !isHomepage, productionArea);
  const { data: hierarchy, isLoading: hierarchyLoading } =
    useGetEquipmentHierarchy(isHomepage);

  if (isLoading || hierarchyLoading) {
    return { productionArea: undefined, isLoading: true };
  }

  if (isHomepage && hierarchy) {
    const productionAreas = hierarchy.flatMap((sg: SiteGroup) =>
      sg.plants.map((p) => p.productionArea)
    );
    return {
      productionArea: mostFrequentString(productionAreas),
      isLoading: false,
    };
  } else if (data) {
    return {
      productionArea: data.productionArea as ProductionArea,
      isLoading: false,
    };
  }

  return { productionArea: undefined, isLoading: false };
};

const mostFrequentString = (strings: string[]): ProductionArea => {
  if (strings.length === 0) return ProductionArea.Grinding;

  const frequencyMap: Record<string, number> = {};
  let maxCount = 0;
  let mostFrequent: string | null = null;

  for (const str of strings) {
    frequencyMap[str] = (frequencyMap[str] || 0) + 1;
    if (frequencyMap[str] > maxCount) {
      maxCount = frequencyMap[str];
      mostFrequent = str;
    }
  }

  return (mostFrequent ?? ProductionArea.Grinding) as ProductionArea;
};
